import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useWhatsAppLink, type UseWhatsAppLinkResult } from "@/hooks/useWhatsAppLink";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ConnectionBrandMark } from "@/components/connections/ConnectionBrandMark";
import ConnectionManageSurface from "@/components/connections/ConnectionManageSurface";

interface WhatsAppLinkCardProps {
  hideWhenConnected?: boolean;
}

interface ViewProps extends WhatsAppLinkCardProps {
  link: UseWhatsAppLinkResult;
}

export function WhatsAppLinkCardView({ hideWhenConnected = false, link }: ViewProps) {
  const { t } = useLanguage();
  const { connected, loading, activating, activate, disconnect, statusKnown = true, refresh } = link;
  const reduceMotion = useReducedMotion();
  const [manageOpen, setManageOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  if (loading || (hideWhenConnected && connected)) return null;

  const onActivate = async () => {
    try {
      await activate("settings_connections");
      toast.success(t("whatsappLink.opening"));
    } catch {
      toast.error(t("whatsappLink.error"));
    }
  };

  const onDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnect();
      toast.success(t("whatsappLink.disconnected"));
      setManageOpen(false);
    } catch {
      toast.error(t("whatsappLink.error"));
    } finally {
      setDisconnecting(false);
      setDisconnectOpen(false);
    }
  };

  return (
    <>
      <motion.article
        data-provider-card="whatsapp"
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="group flex min-h-[156px] flex-col rounded-2xl border border-border/80 bg-card p-4 shadow-sm transition-colors hover:border-primary/30 sm:p-5"
      >
        <div className="flex min-w-0 items-start gap-3">
          <ConnectionBrandMark provider="whatsapp" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="truncate text-base font-semibold text-foreground sm:text-base">{t("whatsappLink.title")}</h2>
              {connected && statusKnown && <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />}
            </div>
            <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
              {t("connections.benefit.whatsapp")}
            </p>
          </div>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <span className={`text-xs font-medium ${connected && statusKnown ? "text-emerald-500" : "text-muted-foreground"}`}>
            {t(!statusKnown ? "connections.ux.statusUnknown" : connected ? "connections.ux.connected" : "connections.ux.notConnected")}
          </span>
          {!statusKnown ? <button type="button" className="min-h-11 rounded-xl border border-border px-4 py-2 text-sm font-semibold" onClick={() => void refresh()}>{t("common.retry")}</button> : connected ? (
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              className="inline-flex min-h-11 items-center gap-1 rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              {t("connections.ux.manage")}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void onActivate()}
              disabled={activating}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2 text-sm font-semibold text-[#063d20] disabled:opacity-50"
            >
              {activating && <Loader2 className="h-4 w-4 animate-spin" />}
              {activating ? t("whatsappLink.opening") : t("connections.ux.connect")}
            </button>
          )}
        </div>
      </motion.article>

      <ConnectionManageSurface
        open={manageOpen}
        onOpenChange={setManageOpen}
        title={t("whatsappLink.title")}
        description={t("whatsappLink.connectedDesc")}
        closeLabel={t("common.close")}
        brand={<ConnectionBrandMark provider="whatsapp" size="lg" />}
      >
        <div className="space-y-4 pt-1">
          <div className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/30 px-3 py-2.5">
            <span className="text-sm text-muted-foreground">{t("connections.ux.status")}</span>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-500">
              <Check className="h-4 w-4" />
              {t("connections.ux.connected")}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{t("whatsappLink.connectedDesc")}</p>
          <button
            type="button"
            onClick={() => setDisconnectOpen(true)}
            className="w-full rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
          >
            {t("whatsappLink.disconnect")}
          </button>
        </div>
      </ConnectionManageSurface>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("whatsappLink.disconnectConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("whatsappLink.disconnectConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => { event.preventDefault(); void onDisconnect(); }}
              disabled={disconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("whatsappLink.disconnect")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function WhatsAppLinkCard(props: WhatsAppLinkCardProps) {
  const link = useWhatsAppLink();
  return <WhatsAppLinkCardView {...props} link={link} />;
}
